import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchPortTypes } from '../data-provider';
import type { PortType, PortTypeFilters, PortTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal port type lookup cache used to resolve port type references
 * without repeatedly requesting the same port type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const portTypesQueryOptions = (
  filters: Partial<PortTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const portTypes = await fetchPortTypes(filters, { signal });
      put(portTypes.list);
      return portTypes;
    },
    queryKey: CACHE_KEYS.portTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_PORT_TYPES_CACHE_KEY = btoa('internal-port-types-cache-key');

const INTERNAL_QUERY_PORT_TYPES_KEY = portTypesQueryOptions({ codeDesc: INTERNAL_PORT_TYPES_CACHE_KEY }).queryKey;

const getPortTypeFallback = (portTypeId: string): PortType => {
  logger.warn(`Port type "${portTypeId}" was not found. Verify the data model.`);
  return {
    id: portTypeId,
    label: portTypeId,
  };
};

export const put = async (portTypes: PortType[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_PORT_TYPES_KEY);

  const newPortTypes = portTypes.reduce(
    (acc, portType) => {
      acc[portType.id] = portType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_PORT_TYPES_KEY, {
    list: Object.values(newPortTypes),
    map: newPortTypes,
  });
};

export const getAll = (): PortTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_PORT_TYPES_KEY);
};

export const find = async (portTypeId: string): Promise<PortType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PORT_TYPES_KEY);

  const portType = data?.map[portTypeId];
  if (portType) {
    return portType;
  }

  try {
    const newData = await queryClient.query(portTypesQueryOptions({ search: { codeId: portTypeId } }));
    if (newData.map[portTypeId]) {
      void put(newData.list);
      return newData.map[portTypeId];
    }
  } catch {
    return getPortTypeFallback(portTypeId);
  }

  return getPortTypeFallback(portTypeId);
};

export const findAll = async (portTypeIds: string[]): Promise<PortType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_PORT_TYPES_KEY);

  const missingIds: string[] = [];
  const portTypes: PortType[] = [];

  for (const portTypeId of portTypeIds) {
    const pt = data?.map[portTypeId];
    if (pt) {
      portTypes.push(pt);
    } else {
      missingIds.push(portTypeId);
    }
  }

  if (!missingIds.length) {
    return portTypes;
  }

  try {
    const newData = await queryClient.query(portTypesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const pt = newData.map[missingId];
      if (pt) {
        portTypes.push(pt);
      } else {
        portTypes.push(getPortTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      portTypes.push(getPortTypeFallback(missingId));
    }
  }

  return portTypes;
};
